<!--
  Tracked — what the crew's Daily Reports were costed at, at the owner's own
  rates. In his words, "what we think we are paying."

  This section is one of two on the Total Breakdown showing money, and it is
  the one that has never been near a bank. Every figure here comes from
  /api/admin/pnl (the same pricing the Performance Dashboard runs), never from
  an imported statement — which is why the heading says WHAT WE COSTED and
  says it in the subtitle rather than in a tooltip. The section directly below
  this one says WHAT ACTUALLY MOVED and is drawn from statements alone. A
  reader who cannot tell at a glance which of the two he is looking at will
  eventually add them together, and adding them together is the double count
  this whole screen was rebuilt to remove.

  Its own component, and not another six hundred lines of AdminBreakdown,
  because the screen now carries four sections and one file holding all four
  is a file nobody can check the sourcing of.

  Formatting: this component formats its own figures with `money`/`percent`
  and hands BarChart nothing but finished strings — the charts under ../charts
  never format, so the number on a bar and the number in the figure row above
  it are character-for-character the same.
-->
<script lang="ts">
  import { money, moneyAxis, percent } from '../../../lib/money';
  import type { TrackedFigures, TrackedMonth } from '../../../lib/types';
  import BarChart from '../charts/BarChart.svelte';
  import { shortMonthLabel } from '../scope';

  interface Props {
    /** The twelve months the chart draws, oldest first, zero-filled. */
    months: readonly TrackedMonth[];
    /**
     * The period's own totals. Null — and shown as dashes rather than as
     * zeros — while the months have not arrived or did not come back.
     */
    figures: TrackedFigures | null;
    /** The period these figures cover, spelled the way a person says it. */
    period: string;
    /** Company (all), or the one job the picker has narrowed to. */
    scope: string;
    /** A year's own twelve months, rather than the twelve ending at a month. */
    isYear: boolean;
    /** Asks the screen to fetch the costed months again. */
    onretry: () => void;
    loading: boolean;
    failed: boolean;
  }

  const { months, figures, period, scope, isYear, onretry, loading, failed }: Props = $props();

  const SERIES = [
    { key: 'revenue', label: 'Revenue' },
    { key: 'cost', label: 'Cost' }
  ] as const;

  /*
   * A run of months with no Daily Report in any of them. The route zero-fills
   * on purpose — a month with no reports is a real point on the chart, not a
   * gap that would shift every bar after it — but a wall of $0.00 with no
   * sentence beside it reads as "this job lost everything" rather than as
   * "nothing has been filed against it". Same rule the bank half follows for
   * an empty period.
   */
  const nothingCosted = $derived(
    months.length > 0 && months.every((month) => month.revenue === 0 && month.cost === 0)
  );

  /*
   * The figures row above the chart is NOT the same twelve months as
   * `nothingCosted` checks — it is only the picked period (the last of the
   * twelve when scoped to a month; see AdminBreakdown's `trackedFigures`).
   * Open the screen on the 2nd of a month before the first Daily Report is
   * filed and the eleven months either side can be full of real bars while
   * this period alone is all zero — the exact wall of $0.00 the header
   * comment warns against, and `nothingCosted` alone would miss it because
   * the OTHER eleven months are not empty. Guarded on its own account.
   */
  const nothingInPeriod = $derived(
    figures !== null && figures.revenue === 0 && figures.cost === 0
  );

  const groups = $derived(
    months.map((point) => ({
      key: point.month,
      label: shortMonthLabel(point.month),
      bars: [
        { series: 'revenue', value: point.revenue, display: money(point.revenue) },
        { series: 'cost', value: point.cost, display: money(point.cost) }
      ],
      line: point.margin,
      lineDisplay: money(point.margin)
    }))
  );
</script>

<section class="a-panel" aria-labelledby="tracked-title">
  <div class="a-panel__head">
    <h2 class="a-panel__title" id="tracked-title">Tracked</h2>
    <p class="a-panel__sub">What we costed · {period} · {scope} · ex-GST</p>
  </div>

  <p class="a-panel__note">
    Priced out of your crew's Daily Reports at your own rates — what you think you are paying.
    No bank line is in any figure here.
  </p>

  {#if failed}
    <!--
      Its own button. This section fetches on its own and fails on its own,
      and it raises no toast when it does — so without this the only way back
      is a page reload, which is not a thing a screen should ask for.
    -->
    <p class="meta">The costed figures for {period} did not come back. Nothing is wrong with them — the request failed.</p>
    <p>
      <button type="button" class="a-cta" onclick={onretry} disabled={loading}>
        {loading ? 'Trying…' : 'Try again'}
      </button>
    </p>
  {:else}
    {#if nothingCosted}
      <p class="meta">
        No Daily Report covers {scope} in these twelve months. The zeros below are the absence of
        reports, not a period that earned nothing.
      </p>
    {:else if nothingInPeriod}
      <p class="meta">
        No Daily Report covers {scope} in {period} yet. The zeros below are the absence of a report
        for this period alone — at least one other month in the chart below has real figures.
      </p>
    {/if}

    <dl class="figs">
      <div>
        <dt>Revenue</dt>
        <dd class="a-num">{money(figures?.revenue)}</dd>
      </div>
      <div>
        <dt>Cost</dt>
        <dd class="a-num">{money(figures?.cost)}</dd>
      </div>
      <div>
        <dt>Profit</dt>
        <dd class="a-num" class:is-bad={(figures?.margin ?? 0) < 0}>{money(figures?.margin)}</dd>
      </div>
      <div>
        <dt>Margin</dt>
        <dd class="a-num" class:is-bad={(figures?.margin_pct ?? 0) < 0}>{percent(figures?.margin_pct)}</dd>
      </div>
    </dl>

    {#if loading && months.length === 0}
      <p class="meta"><span class="spinner" aria-hidden="true"></span> Loading what we costed…</p>
    {:else}
      <div class="serieskey">
        {#each SERIES as se, i (se.key)}
          <span class="serieskey__item">
            <span class="chart-swatch" style:background="var(--chart-{i + 1})" aria-hidden="true"></span>
            {se.label}
          </span>
        {/each}
        <span class="serieskey__item">
          <span class="serieskey__line" aria-hidden="true"></span>
          Profit
        </span>
      </div>
      <BarChart
        {groups}
        series={SERIES}
        caption="Costed revenue and cost by month, with profit"
        lineLabel="Profit"
        tickLabel={moneyAxis}
      />
      <p class="a-panel__note">
        {isYear ? `The twelve months of ${period}` : `The twelve months ending ${period}`} · {scope} · from Daily
        Reports.
      </p>
    {/if}
  {/if}
</section>

<style>
  /* The period's four figures, sized to be read from across a desk — this is
     the first thing the owner looks at on the screen. */
  .figs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(8rem, 1fr));
    gap: 0.5rem 1rem;
    margin: 0;
    padding: 0.6rem 0.75rem;
    background: var(--bg-sunken);
    border-radius: var(--radius);
  }
  .figs div { display: grid; gap: 0.05rem; }
  .figs dt {
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-faint);
  }
  .figs dd {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 800;
    letter-spacing: -0.01em;
  }
  /* A loss is a figure, not a fault — but it must not read as a win. */
  .is-bad { color: var(--hold); }

  /* What each bar colour means. BarChart draws no legend of its own — it has
     no formatted strings to put in one — so the key is built here from the
     same ramp variables the bars read. */
  .serieskey {
    display: flex;
    flex-wrap: wrap;
    gap: 0.3rem 0.9rem;
    margin: 0;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--ink-soft);
  }
  .serieskey__item {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }
  .serieskey__line {
    display: inline-block;
    width: 0.9rem;
    height: 0;
    border-top: 2px solid var(--brand-dark);
    flex: 0 0 auto;
  }
</style>
